import React from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import { FaPen, FaMobile, FaMobileScreenButton } from "react-icons/fa6";
import { DEFAULT_AVATAR } from "shared/config/properties";
import { getPatientInfo } from "requests/Patient";
import { useAuth } from "shared/providers/auth";
import { IPatientInfoData } from "shared/types/Patient";

// Exporta para compatibilidade com código existente
export type PatientInfoData = IPatientInfoData;

interface PatientInfoModalProps {
  open: boolean;
  onClose: () => void;
  patientId: number;
  // Callback para editar tipo de aparelho (opcional)
  onEditBraceType?: () => void;
}

const PatientInfoModal: React.FC<PatientInfoModalProps> = ({
  open,
  onClose,
  patientId,
  onEditBraceType,
}) => {
  const { user } = useAuth();
  const [patient, setPatient] = React.useState<IPatientInfoData | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  // Busca as informações do paciente quando o modal abre
  React.useEffect(() => {
    const fetchPatientInfo = async () => {
      if (!open || !patientId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const { data, status } = await getPatientInfo(user.data.id, patientId);
        
        if ([200, 201].includes(status) && data.success) {
          setPatient(data.data);
        } else {
          setError(data.message || "Erro ao buscar informações do paciente");
        }
      } catch (err) {
        console.error("Erro ao buscar informações do paciente:", err);
        setError("Erro ao buscar informações do paciente");
      } finally {
        setLoading(false);
      }
    };

    fetchPatientInfo();
  }, [open, patientId, user.data.id]);

  if (!open) return null;

  // Determina o texto do tipo de aparelho
  const getBraceTypeText = () => {
    if (!patient) return "Não definido";
    switch (patient.type) {
      case 1:
        return "Fixo";
      case 2:
        return "Móvel";
      case 3:
        return "Invisível";
      default:
        return "Não definido";
    }
  };

  const renderBenefits = (benefits: string) => {
    const items = benefits
      .split("+")
      .map((item) => item.trim())
      .filter(Boolean);

    if (!items.length) return null;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          textAlign: "right",
          gap: "4px",
        }}
      >
        {items.map((item, index) => (
          <Typography
            key={index}
            sx={{ color: "#042b7e", fontWeight: "600", fontSize: "14px" }}
          >
            {item}
          </Typography>
        ))}
      </Box>
    );
  };

  // Helper para renderizar um item de informação
  const InfoItem = ({
    label,
    value,
    backgroundColor = "#f5f7fa",
    valueColor = "#042b7e",
    onClick,
    rightElement,
  }: {
    label: string;
    value: string | React.ReactNode;
    backgroundColor?: string;
    valueColor?: string;
    onClick?: () => void;
    rightElement?: React.ReactNode;
  }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 16px",
        backgroundColor,
        borderRadius: "10px",
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { backgroundColor: "#e8f4fd" } : {},
      }}
      onClick={onClick}
    >
      <Typography sx={{ color: "#666", fontSize: "14px" }}>
        {label}
      </Typography>
      {rightElement || (
        <Typography sx={{ color: valueColor, fontWeight: "600", fontSize: "14px" }}>
          {value}
        </Typography>
      )}
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "500px",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: 24,
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #042b7e 0%, #007DED 100%)",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          {loading || !patient ? (
            <CircularProgress sx={{ color: "white" }} />
          ) : (
            <>
              <Avatar
                src={patient.pac_avatar || DEFAULT_AVATAR}
                sx={{ width: "80px", height: "80px", border: "3px solid white" }}
                alt={patient.pac_name}
              />
              <Typography
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "20px",
                  textAlign: "center",
                }}
              >
                {patient.pac_name}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: "14px",
                }}
              >
                ID {patient.pac_id_fant}
              </Typography>
            </>
          )}
        </Box>

        {/* Content - Scrollable */}
        <Box
          sx={{
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            overflowY: "auto",
            flexGrow: 1,
            minHeight: "200px",
            justifyContent: loading || error ? "center" : "flex-start",
            alignItems: loading || error ? "center" : "stretch",
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Typography sx={{ color: "#d32f2f", textAlign: "center" }}>
              {error}
            </Typography>
          ) : patient ? (
            <>
              {/* CPF */}
              {patient.cpf && (
                <InfoItem label="CPF" value={patient.cpf} />
              )}

          {/* Situação (para busca de pacientes) */}
          {patient.situation_label && (
            <InfoItem label="Situação" value={patient.situation_label} />
          )}

          {/* Dentista Responsável */}
          {patient.Name_detRel && (
            <InfoItem label="Dentista Responsável" value={patient.Name_detRel} />
          )}

          {/* Plano Contratado */}
          {patient.aparelhos && (
            <InfoItem label="Plano Contratado" value={patient.aparelhos} />
          )}

          {/* Benefícios */}
          {patient.beneficios && (
            <InfoItem
              label="Benefícios"
              value=""
              rightElement={renderBenefits(patient.beneficios)}
            />
          )}

          {/* Data de Vencimento */}
          {patient.dia_vcto && (
            <InfoItem label="Data de Vencimento" value={`Dia ${patient.dia_vcto}`} />
          )}

          {/* Tipo de Aparelho */}
          {patient.type !== undefined && (
            <InfoItem
              label="Tipo de Aparelho"
              value={getBraceTypeText()}
              onClick={onEditBraceType}
              rightElement={
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Typography sx={{ color: "#042b7e", fontWeight: "600", fontSize: "14px" }}>
                    {getBraceTypeText()}
                  </Typography>
                  {onEditBraceType && <FaPen color="#007DED" size={12} />}
                </Box>
              }
            />
          )}

          {/* Controle de Contenção */}
          {patient.proc_name === "Controle de Contenção" && patient.qtd_contencao !== undefined && (
            <InfoItem
              label="Controle de Contenção"
              value={`${patient.qtd_contencao}/8`}
              backgroundColor="#e3f2fd"
              valueColor="#1565c0"
            />
          )}

          {/* Status do App */}
          <InfoItem
            label="App Cliente"
            backgroundColor={patient.has_app ? "#e8f5e9" : "#fff3e0"}
            rightElement={
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {patient.has_app ? (
                  <>
                    <FaMobileScreenButton color="#4caf50" />
                    <Typography sx={{ color: "#2e7d32", fontWeight: "600", fontSize: "14px" }}>
                      Instalado
                    </Typography>
                  </>
                ) : (
                  <>
                    <FaMobile color="#ff9800" />
                    <Typography sx={{ color: "#e65100", fontWeight: "600", fontSize: "14px" }}>
                      Não instalado
                    </Typography>
                  </>
                )}
              </Box>
            }
            value=""
          />

          {/* Detalhes do App */}
          {patient.has_app && patient.app_info && (
            <>
              {patient.app_info.ultimo_acesso && (
                <InfoItem label="Último Acesso" value={patient.app_info.ultimo_acesso} />
              )}
            </>
          )}

          {/* Consultas Atendidas */}
          {patient.attended_consultations && patient.attended_consultations.total_attended > 0 && (
            <InfoItem
              label="Consultas Atendidas"
              backgroundColor="#e8f5e9"
              rightElement={
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Typography
                    sx={{
                      color: "#2e7d32",
                      fontWeight: "700",
                      fontSize: "16px",
                      backgroundColor: "#c8e6c9",
                      padding: "4px 12px",
                      borderRadius: "20px",
                    }}
                  >
                    {patient.attended_consultations.total_attended}
                  </Typography>
                  {patient.attended_consultations.treatment_start_formatted && (
                    <Typography sx={{ color: "#666", fontSize: "12px" }}>
                      desde {patient.attended_consultations.treatment_start_formatted}
                    </Typography>
                  )}
                </Box>
              }
              value=""
            />
          )}

          {/* Condições Neurológicas */}
          {patient.has_neuro && patient.neuro_info && (
            <Box
              sx={{
                padding: "12px 16px",
                backgroundColor: "#e3f2fd",
                borderRadius: "10px",
                borderLeft: "4px solid #1976d2",
              }}
            >
              <Typography
                sx={{ color: "#1565c0", fontWeight: "600", fontSize: "14px", marginBottom: "8px" }}
              >
                ⚠️ Condições Neurológicas
              </Typography>
              {patient.neuro_info.conditions.map((condition, index) => (
                <Typography key={index} sx={{ color: "#666", fontSize: "13px", marginLeft: "16px" }}>
                  • {condition}
                </Typography>
              ))}
              {patient.neuro_info.other_conditions && (
                <Typography sx={{ color: "#666", fontSize: "13px", marginTop: "8px" }}>
                  📝 {patient.neuro_info.other_conditions}
                </Typography>
              )}
            </Box>
          )}
            </>
          ) : null}
        </Box>

        {/* Footer */}
        <Box sx={{ padding: "16px 24px", borderTop: "1px solid #eee", flexShrink: 0 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: "#042b7e",
              borderRadius: "10px",
              padding: "12px",
              textTransform: "none",
              fontWeight: "600",
              "&:hover": {
                backgroundColor: "#031d54",
              },
            }}
          >
            Fechar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export { PatientInfoModal };
